import React from "react";
import "@/App.css";
import { BrowserRouter, Link, Route, Routes } from "react-router-dom";

import { ContentProvider } from "./context/ContentContext";

import Navbar from "./components/common/Navbar";
import Footer from "./components/common/Footer";
import GDPRBanner from "./components/common/GDPRBanner";
import WhatsAppButton from "./components/common/WhatsAppButton";
import ScrollManager from "./components/common/ScrollManager";

import HeroSection from "./components/sections/HeroSection";
import { useSEO } from "./hooks/useSeo";
import { createHomeSchema, createSectionSchema, pageSeo, sectionLinks } from "./lib/seo";

const AboutSection = React.lazy(() => import("./components/sections/AboutSection"));
const ServicesSection = React.lazy(() => import("./components/sections/ServicesSection"));
const WhyUsSection = React.lazy(() => import("./components/sections/WhyUsSection"));
const ProjectsSection = React.lazy(() => import("./components/sections/ProjectsSection"));
const CompaniesSection = React.lazy(() => import("./components/sections/CompaniesSection"));
const ContactSection = React.lazy(() => import("./components/sections/ContactSection"));
const BlogSection = React.lazy(() => import("./components/sections/BlogSection"));
const BlogDetailPage = React.lazy(() => import("./pages/BlogDetailPage"));

const SiteLayout = ({ children }) => {
  const handleGDPRAccept = () => {
    window.dispatchEvent(new Event("gdpr-accepted"));
  };

  return (
    <div className="flex min-h-screen flex-col bg-[#0f1a2e]">
      <Navbar />
      <main className="flex-grow">{children}</main>
      <Footer />
      <GDPRBanner onAccept={handleGDPRAccept} />
      <WhatsAppButton />
    </div>
  );
};

const DeferredSection = ({ children, minHeight = 900 }) => {
  const [isMounted, setIsMounted] = React.useState(false);
  const markerRef = React.useRef(null);

  React.useEffect(() => {
    if (isMounted) {
      return undefined;
    }

    const idleTimer = window.setTimeout(() => setIsMounted(true), 8000);

    if (!("IntersectionObserver" in window) || !markerRef.current) {
      return () => window.clearTimeout(idleTimer);
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsMounted(true);
          observer.disconnect();
        }
      },
      { rootMargin: "300px 0px" }
    );

    observer.observe(markerRef.current);

    return () => {
      window.clearTimeout(idleTimer);
      observer.disconnect();
    };
  }, [isMounted]);

  if (isMounted) {
    return children;
  }

  return <div ref={markerRef} aria-hidden="true" style={{ minHeight }} />;
};

const SectionFallback = ({ minHeight = 500 }) => (
  <div className="bg-[#0f1a2e]" aria-hidden="true" style={{ minHeight }} />
);

const SinglePageLayout = () => {
  const homeSeo = pageSeo.home;

  useSEO({
    title: homeSeo.title,
    description: homeSeo.description,
    keywords: homeSeo.keywords,
    canonicalPath: homeSeo.path,
    ogType: "website",
    schema: createHomeSchema(),
  });

  return (
    <SiteLayout>
      <HeroSection />
      <DeferredSection minHeight={900}>
        <React.Suspense fallback={<SectionFallback minHeight={900} />}>
          <AboutSection />
        </React.Suspense>
      </DeferredSection>
      <DeferredSection minHeight={1000}>
        <React.Suspense fallback={<SectionFallback minHeight={1000} />}>
          <ServicesSection />
        </React.Suspense>
      </DeferredSection>
      <DeferredSection minHeight={850}>
        <React.Suspense fallback={<SectionFallback minHeight={850} />}>
          <WhyUsSection />
        </React.Suspense>
      </DeferredSection>
      <DeferredSection minHeight={800}>
        <React.Suspense fallback={<SectionFallback minHeight={800} />}>
          <ProjectsSection />
        </React.Suspense>
      </DeferredSection>
      <DeferredSection minHeight={800}>
        <React.Suspense fallback={<SectionFallback minHeight={800} />}>
          <CompaniesSection />
        </React.Suspense>
      </DeferredSection>
      <DeferredSection minHeight={1000}>
        <React.Suspense fallback={<SectionFallback minHeight={1000} />}>
          <BlogSection />
        </React.Suspense>
      </DeferredSection>
      <DeferredSection minHeight={900}>
        <React.Suspense fallback={<SectionFallback minHeight={900} />}>
          <ContactSection />
        </React.Suspense>
      </DeferredSection>
    </SiteLayout>
  );
};

const RelatedSeoLinks = ({ currentKey }) => {
  const links = sectionLinks.filter((link) => link.id !== currentKey && link.id !== "home");

  return (
    <nav className="bg-[#0f1a2e] px-4 pb-16 sm:px-6 lg:px-8" aria-label="Related sections">
      <div className="mx-auto max-w-7xl border-t border-[#2a3a5a] pt-8">
        <p className="mb-4 text-sm font-semibold uppercase tracking-[0.14em] text-[#f08a95]">
          Explore Vinayak Enterprise
        </p>
        <div className="flex flex-wrap gap-3">
          {links.map((link) => (
            <Link
              key={link.id}
              to={link.href}
              className="rounded-lg border border-[#2a3a5a] bg-[#1a2744] px-4 py-2 text-sm font-medium text-gray-200 transition-colors hover:border-[#c23a4a] hover:text-white"
            >
              {link.label}
            </Link>
          ))}
        </div>
      </div>
    </nav>
  );
};

const SectionRouteLayout = ({ pageKey, SectionComponent }) => {
  const seo = pageSeo[pageKey];

  useSEO({
    title: seo.title,
    description: seo.description,
    keywords: seo.keywords,
    canonicalPath: seo.path,
    ogType: "website",
    schema: createSectionSchema(pageKey),
  });

  return (
    <SiteLayout>
      <div className="pt-16">
        <React.Suspense fallback={<SectionFallback />}>
          <SectionComponent headingLevel="h1" standalone />
        </React.Suspense>
      </div>
      <RelatedSeoLinks currentKey={pageKey} />
    </SiteLayout>
  );
};

const BlogRouteLayout = () => (
  <SiteLayout>
    <React.Suspense fallback={<SectionFallback />}>
      <BlogDetailPage />
    </React.Suspense>
  </SiteLayout>
);

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <ContentProvider>
          <ScrollManager />
          <Routes>
            <Route path="/" element={<SinglePageLayout />} />
            <Route
              path="/about"
              element={<SectionRouteLayout pageKey="about" SectionComponent={AboutSection} />}
            />
            <Route
              path="/services"
              element={<SectionRouteLayout pageKey="services" SectionComponent={ServicesSection} />}
            />
            <Route
              path="/projects"
              element={<SectionRouteLayout pageKey="projects" SectionComponent={ProjectsSection} />}
            />
            <Route
              path="/companies"
              element={<SectionRouteLayout pageKey="companies" SectionComponent={CompaniesSection} />}
            />
            <Route
              path="/contact"
              element={<SectionRouteLayout pageKey="contact" SectionComponent={ContactSection} />}
            />
            <Route path="/blogs/:slug" element={<BlogRouteLayout />} />
            <Route path="*" element={<SinglePageLayout />} />
          </Routes>
        </ContentProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
